import { differenceInMonths } from 'date-fns';
import { assertEvent, assign, fromPromise, setup } from 'xstate';
import { getScreenerForAge } from 'domains/signup/screener/data/screener';
import { TypeTherapyScreenerV2 } from 'types/contentful';
import { TherapyTarget } from 'domains/signup/data/types';
import { ScreenerResults, calculateScreenerResults } from 'domains/signup/screener/data/results';
import { saveResultsToStorage } from 'domains/signup/screener/data/localStorage';
import i18n from 'i18n';
import { MachineStateType } from 'utils/xstate/types';

export type CompletedScreenerMachineContext = {
  target: TherapyTarget;
  dob: Date;
  therapyScreener: TypeTherapyScreenerV2;
  screenerResponses: string[];
  screenerResults: ScreenerResults;
  currentQuestionIndex: number;
};

export const screenerMachine =
  /** @xstate-layout N4IgpgJg5mDOIC5SwMYCcxgHZjQWQEMUALASxwDpsAXXAEQHsAjAYlgFcmBbU6xpgNoAGALqJQABwaxepBlnEgAHogCMAZnUUAHABZtAVgMA2A6sMHdAdgA0IAJ6IATEO0UnBp6qu7dnqwYAnJoAviF2qBjYuIQk5GAUADYMBBDkUADK6Jg4aCwQ8gnkAG4MANYJkTkxRGSUyanpWVG5CCUMKATUcljCIn2KUjLd8ooqCFZuxqq65qpmFrrGgXaOCNaqFH5CGrpOVlbquuoBYRHZ0fi18UkpaViZF7ksuGgMaBQSiV0AZu9cFCql1idQSDXujxauDaWFKnRGvVEAyQICGslGKPG6mMTgopiEgUCHisQisgWmq0Q2KsFECBiEpPUTj25lO4RAQNyIJuaHYWCwTSeuAAiuw4AjYGxODxqM1qmgAEpwKRYWBwZGSaTohSYxABHTaKzTQI7RbLSkTVSbKxOQnaE7abRCZnGM4coVXOKUGi4AAqBDQMGoUu4vH9gbA1A1qK1CLGag8W3M2mWpqMSxWDkQAFp1AZ3JY-EZjMZdEIcQY3Zyal6Ej60PwWEwiGVo2i47qELjjCdjWnLMZDQYLdnVLj-NpguXS+WPGF2VgGBA4Ipq57QYNYz14whs34KNinOonYFtDM-OpVCPmTSPL4jCYZxWqx7ud6sLQG8xN8Nt52ZpsPZGqoJrmOmthZggY6bOSgSTEEmhGEIoTsmub5gncgpQmgP7ajupgUKo5bYqBZqZmsRy4nSDKHMyXhDi+2HoRQvL8lh8qiuKPTwCi7Z-qA4wHDo3jqM6yGBCBBiXhaOJaCmvglsak50ox8rMYucqXAAYgwfIQLhHYCYgFZbCmQRmEIBgpraFrYsYWz0oydGspWqGvtclA-OQpCwMQkAGfxygJvmRE9qmYGWDaFqGEIFDOmORIlnBJjaKpwIeXWH5+gGQYBRiRlQZo7gppZpgRdYI7zIEFABPexZPnO85AA */
  setup({
    types: {
      events: {} as
        | { type: 'startScreener' }
        | { type: 'submitTarget'; target: TherapyTarget }
        | { type: 'submitDob'; dob: Date }
        | { type: 'submitScreenerResponses'; responses: string[] }
        | { type: 'questionIndexChanged'; index: number }
        | { type: 'back' },
      context: {} as Partial<CompletedScreenerMachineContext>,
    },

    actions: {
      saveTarget: assign(({ event }) => {
        assertEvent(event, 'submitTarget');
        return {
          target: event.target,
        };
      }),
      saveDob: assign(({ event }) => {
        assertEvent(event, 'submitDob');
        return {
          dob: event.dob,
        };
      }),
      saveScreenerResponses: assign(({ context, event }) => {
        assertEvent(event, 'submitScreenerResponses');
        const { therapyScreener, target } = context as CompletedScreenerMachineContext;
        const screenerResults = calculateScreenerResults(therapyScreener, event.responses, target);

        saveResultsToStorage(screenerResults);

        return {
          screenerResponses: event.responses,
          screenerResults,
        };
      }),
      saveTherapyScreener: assign((_, params: { data: TypeTherapyScreenerV2 }) => ({
        therapyScreener: params.data,
      })),
      saveQuestionIndex: assign(({ event }) => {
        assertEvent(event, 'questionIndexChanged');
        return {
          currentQuestionIndex: event.index,
        };
      }),
    },
    actors: {
      loadScreenerQuestions: fromPromise(async ({ input }: { input: { dob: Date } }) => {
        const ageInMonths = differenceInMonths(new Date(), input.dob);
        return getScreenerForAge(ageInMonths, i18n.language);
      }),
    },
  }).createMachine({
    id: 'screenerMachine',

    initial: 'landingPage',

    states: {
      landingPage: {
        on: {
          startScreener: {
            target: 'enterTarget',
          },
        },
      },

      enterDob: {
        on: {
          submitDob: [
            {
              target: 'loadingScreener',
              actions: 'saveDob',
            },
          ],
          back: 'enterTarget',
        },
      },

      loadingScreener: {
        invoke: {
          src: 'loadScreenerQuestions',
          input: ({ context: { dob } }) => ({ dob: dob as Date }),
          onDone: {
            target: 'runningScreenerQuestions',
            actions: {
              type: 'saveTherapyScreener',
              params: ({ event }) => ({ data: event.output }),
            },
          },
          onError: 'noScreenerFound',
        },
      },

      runningScreenerQuestions: {
        on: {
          submitScreenerResponses: {
            target: 'finished',
            actions: ['saveScreenerResponses'],
          },
          questionIndexChanged: {
            actions: ['saveQuestionIndex'],
          },
        },
      },

      noScreenerFound: {},

      finished: {
        type: 'final',
      },

      enterTarget: {
        on: {
          submitTarget: {
            target: 'enterDob',
            actions: 'saveTarget',
          },
        },
      },
    },
  });

export type ScreenerState = MachineStateType<typeof screenerMachine>;
